import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Loader2, X, FileText, Image, Calculator } from 'lucide-react';
import api, { getQuotationsByStyleNo } from '@/utils/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EnhancedExportModal({ open, onClose, task }) {
  const [includesSummary, setIncludesSummary] = useState(true);
  const [includesCad, setIncludesCad] = useState(false);
  const [includesQuotation, setIncludesQuotation] = useState(false);
  const [quotationVersions, setQuotationVersions] = useState([]);
  const [selectedQuotationId, setSelectedQuotationId] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (open && task?.style_no) {
      setLoading(true);
      getQuotationsByStyleNo(task.style_no)
        .then(res => {
          setQuotationVersions(res.data || []);
          if (res.data?.length > 0) {
            setSelectedQuotationId(res.data[res.data.length - 1].id);
            setSelectedQuotation(res.data[res.data.length - 1]);
          }
        })
        .catch(() => setQuotationVersions([]))
        .finally(() => setLoading(false));
    } else {
      setQuotationVersions([]);
    }
  }, [open, task?.style_no]);

  useEffect(() => {
    const q = quotationVersions.find(v => v.id === selectedQuotationId);
    setSelectedQuotation(q || null);
  }, [selectedQuotationId, quotationVersions]);

  const handleExport = async () => {
    if (!task?.id) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set('include_summary', includesSummary ? 'true' : 'false');
      params.set('include_cad', includesCad ? 'true' : 'false');

      const response = await api.get(
        `${BACKEND_URL}/api/tasks/${task.id}/export-pdf?${params.toString()}`,
        { responseType: 'blob' }
      );

      // Determine filename from content-disposition header or generate one
      const disposition = response.headers['content-disposition'] || '';
      let filename = 'export';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      } else {
        const cad_no = task.internal_cad_no || task.party_cad_no || 'CAD';
        const isZip = includesSummary && includesCad;
        filename = isZip ? `${cad_no}_Export.zip` : `${cad_no}_${includesCad ? 'CAD_Sheet' : 'Summary'}.pdf`;
      }

      // Trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${filename}`);
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  const noneSelected = !includesSummary && !includesCad && !includesQuotation;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center" data-testid="enhanced-export-modal">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-[480px] max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Export Task Package</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7"><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            Select what to export for <strong>{task?.internal_cad_no || task?.style_no || 'this task'}</strong>.
            {includesSummary && includesCad
              ? ' Both will download as a ZIP.'
              : ' Single selection downloads directly as PDF.'}
          </p>

          {/* Summary */}
          <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
            <Checkbox checked={includesSummary} onCheckedChange={setIncludesSummary} data-testid="export-include-summary" />
            <FileText className="h-4 w-4 text-blue-500" />
            <div>
              <p className="text-sm font-medium">Summary Sheet</p>
              <p className="text-xs text-muted-foreground">Full task details, metals, stones, findings, instructions</p>
            </div>
          </label>

          {/* CAD Sheet */}
          <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
            <Checkbox checked={includesCad} onCheckedChange={setIncludesCad} data-testid="export-include-cad" />
            <Image className="h-4 w-4 text-green-500" />
            <div>
              <p className="text-sm font-medium">CAD Sheet</p>
              <p className="text-xs text-muted-foreground">CAD images, design parameters, stone breakdown</p>
            </div>
          </label>

          {/* Quotation */}
          <div className="p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={includesQuotation} onCheckedChange={setIncludesQuotation} data-testid="export-include-quotation" />
              <Calculator className="h-4 w-4 text-amber-500" />
              <div>
                <p className="text-sm font-medium">Quotation</p>
                <p className="text-xs text-muted-foreground">Pricing breakdown</p>
              </div>
            </label>
            {includesQuotation && (
              <div className="ml-10 mt-3">
                {loading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Loading...</div>
                ) : quotationVersions.length === 0 ? (
                  <p className="text-xs text-amber-600">No quotations found</p>
                ) : (
                  <Select value={selectedQuotationId} onValueChange={setSelectedQuotationId}>
                    <SelectTrigger className="h-8 text-xs" data-testid="export-quotation-version-select">
                      <SelectValue placeholder="Select version" />
                    </SelectTrigger>
                    <SelectContent>
                      {quotationVersions.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.ref} - ${v.final_cost_usd?.toFixed(2)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>

          {/* Info about output format */}
          <div className="text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
            {includesSummary && includesCad
              ? 'Downloads as ZIP containing Summary.pdf + CAD_Sheet.pdf'
              : includesSummary
              ? 'Downloads as Summary PDF'
              : includesCad
              ? 'Downloads as CAD Sheet PDF'
              : 'Select at least one option'}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/20">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={exporting || noneSelected}
            className="bg-[hsl(220,60%,20%)] text-white hover:bg-[hsl(220,60%,25%)]"
            data-testid="export-execute-btn"
          >
            {exporting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
            {includesSummary && includesCad ? 'Download ZIP' : 'Download PDF'}
          </Button>
        </div>
      </div>
    </div>
  );
}
